import React from "react";
import { v4 as uuidv4 } from "uuid";
import { useOverlayContext } from "../OverlayContext";
import type { WindowsApi } from "../types";

function renderWindowComponent<TProps>(Component: React.JSXElementConstructor<TProps>, props: TProps) {
    return function OverlayWindowComponent() {
        return React.createElement(Component, props);
    };
}

/**
 * API открытия window-поверхностей с привязкой к lifecycle владельца (unmount → close).
 */
export function useWindows(): WindowsApi {
    const { openOverlay, closeOverlaysByOwner } = useOverlayContext();
    const ownerIdRef = React.useRef<string | null>(null);
    ownerIdRef.current ??= uuidv4();
    const ownerId = ownerIdRef.current;

    React.useEffect(() => {
        return () => {
            closeOverlaysByOwner(ownerId);
        };
    }, [closeOverlaysByOwner, ownerId]);

    const open = React.useCallback<WindowsApi["open"]>(
        (component, props, options = {}) => {
            const { ownerLifecycle = "close-on-owner-unmount", ...overlayOptions } = options;

            return openOverlay({
                surface: "window",
                ownerId: ownerLifecycle === "close-on-owner-unmount" ? ownerId : undefined,
                placement: "fullscreen",
                blocking: false,
                ...overlayOptions,
                render: renderWindowComponent(component, props),
            });
        },
        [openOverlay, ownerId],
    );

    return React.useMemo(() => ({ open }), [open]);
}
